import type { ReferenceContentDetail } from '../api'

export type CcaRecoveryContract = {
  first_failed_stage: string | null
  retry_start_stage: string | null
  recovery_kind: 'capture' | 'media' | 'semantic' | 'none'
  reused_successful_stages: string[]
  invalidated_downstream_stages: string[]
  diagnostic_code: string | null
  retryable: boolean
  recommended_action: string | null
  active_task_id: string | null
  active_task_kind: string | null
  source_recapture_required: boolean
  reference_content_id: string
  original_failed_task_id: string | null
  retry_task_id: string | null
  retry_of_task_id: string | null
  full_rerun_available: boolean
  retry_failed_stages_available: boolean
  active_task_in_flight?: boolean
  execution_state?: 'IDLE' | 'QUEUED' | 'RUNNING' | 'FAILED' | 'SUCCEEDED' | string | null
  effective_harness_id?: string | null
  effective_job_id?: string | null
  job_status?: string | null
  harness_status?: string | null
}

export function recoveryFromDetail(detail: ReferenceContentDetail | null): CcaRecoveryContract | null {
  if (!detail?.recovery) return null
  return detail.recovery as CcaRecoveryContract
}

export function isRecoveryInFlight(detail: ReferenceContentDetail | null): boolean {
  if (!detail) return false
  const recovery = recoveryFromDetail(detail)
  // Prefer Control Plane truth: only show in-flight when Job/harness is
  // actually queued/running. Stale PENDING snapshots with terminal Jobs must
  // not advertise a fake retry.
  if (recovery && recovery.active_task_in_flight === true) return true
  if (recovery && recovery.active_task_in_flight === false) {
    // Explicit false from backend wins for semantic — do not resurrect spinner
    // from stale ai_running stage rows.
    if (recovery.recovery_kind === 'semantic') return false
    const capture = detail.capture?.status
    const media = detail.media_analysis?.status
    if (capture === 'pending' || capture === 'running') return true
    if (media === 'PENDING' || media === 'RUNNING') return true
    return false
  }
  if (detail.analysis_progress?.ai_running) return true
  const capture = detail.capture?.status
  const media = detail.media_analysis?.status
  const analysis = detail.analysis?.status
  if (capture === 'pending' || capture === 'running') return true
  if (media === 'PENDING' || media === 'RUNNING') return true
  if (analysis === 'pending' || analysis === 'running') return true
  return false
}

/** Semantic AI retry should be disabled while an effective attempt is live. */
export function isSemanticRetryBlocked(detail: ReferenceContentDetail | null): boolean {
  if (!detail) return false
  if (isRecoveryInFlight(detail) && recoveryFromDetail(detail)?.recovery_kind === 'semantic') return true
  if (detail.analysis_progress?.ai_running) return true
  return false
}

export function shouldShowFailedStageRetry(detail: ReferenceContentDetail | null): boolean {
  const recovery = recoveryFromDetail(detail)
  if (!recovery) return false
  if (isRecoveryInFlight(detail)) return false
  return recovery.retry_failed_stages_available
}

export function shouldShowFullRerun(detail: ReferenceContentDetail | null): boolean {
  if (!detail) return false
  if (isRecoveryInFlight(detail)) return false
  return detail.status === 'failed' && detail.capture?.status === 'failed'
}
